import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAppDispatch, useAppSelector } from '../hooks';
import { requestPasswordReset, clearPasswordResetStatus } from '../store/slices/authSlice';

const schema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
}).required();

type FormData = {
  email: string;
};

const ForgotPassword = () => {
  const dispatch = useAppDispatch();
  const { isLoading, error, passwordResetStatus, passwordResetRateLimit } = useAppSelector((state) => state.auth);
  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const { register, handleSubmit, formState: { errors }, getValues } = useForm<FormData>({
    resolver: yupResolver(schema)
  });

  const onSubmit = async (data: FormData) => {
    try {
      await dispatch(requestPasswordReset({ email: data.email })).unwrap();
      setSuccess(true);
    } catch (err) {
      // Error is handled by the reducer
    }
  };

  // Handle countdown timer for rate limiting
  useEffect(() => {
    if (passwordResetRateLimit?.reset_in_seconds) {
      setCountdown(passwordResetRateLimit.reset_in_seconds);
      
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            dispatch(clearPasswordResetStatus());
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [passwordResetRateLimit, dispatch]);

  const handleTryAgain = () => {
    setSuccess(false);
    dispatch(clearPasswordResetStatus());
  };

  // Helper function to format time
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (success || passwordResetStatus) {
    const email = getValues('email');
    
    return (
      <div style={{
        width: '100%',
        minHeight: '100vh',
        backgroundColor: '#000000',
        position: 'relative'
      }}>
        <div style={{
          width: '100%',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem 1rem'
        }}>
          <div style={{
            width: '440px',
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center'
            }}>
              <img
                src="/images/ana_logo.png"
                alt="ANA Logo"
                style={{ height: '48px', width: '48px' }}
              />
              <img
                src="/images/ana_text_dark.svg"
                alt="ANA"
                style={{ marginTop: '1rem', height: '32px', width: 'auto' }}
              />
              <h1 style={{
                marginTop: '1.5rem',
                fontSize: '1.5rem',
                fontWeight: 700,
                color: '#FFFFFF',
                marginBottom: 0
              }}>
                Check Your Email
              </h1>
            </div>

            <div style={{
              background: 'rgba(15, 15, 15, 0.6)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '16px',
              padding: '2rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '1.5rem',
              textAlign: 'center'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(34, 197, 94, 0.05))',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#22c55e'
              }}>
                <svg width="32" height="32" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              
              <div>
                <h2 style={{
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  color: '#FFFFFF',
                  margin: 0
                }}>
                  Reset Link Sent!
                </h2>
                <p style={{
                  color: '#9CA3AF',
                  margin: '1rem 0 0 0',
                  lineHeight: 1.6
                }}>
                  We've sent a password reset link to <strong>{email}</strong>. 
                  Please check your email and click the link to reset your password.
                </p>
                <p style={{
                  marginTop: '1rem',
                  fontSize: '0.8rem',
                  color: '#9CA3AF',
                  margin: '1rem 0 0 0'
                }}>
                  Don't see the email? Check your spam folder or try again.
                </p>
              </div>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                width: '100%'
              }}>
                <button
                  onClick={handleTryAgain}
                  style={{
                    width: '100%',
                    padding: '0.5rem 1rem',
                    backgroundColor: '#FF4C00',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#CC3D00'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#FF4C00'}
                >
                  Send Another Email
                </button>
                <Link
                  to="/login"
                  style={{
                    width: '100%',
                    padding: '0.5rem 1rem',
                    background: 'transparent',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    color: '#FFFFFF',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    textDecoration: 'none',
                    display: 'block',
                    textAlign: 'center'
                  }}
                >
                  Back to Login
                </Link>
              </div>
            </div>
          </div>

          <div style={{
            marginTop: '1.5rem',
            textAlign: 'center',
            fontSize: '0.875rem',
            color: '#9CA3AF'
          }}>
            Built with ❤️ by <a href="https://www.openana.ai/" target="_blank" rel="noopener noreferrer" style={{
              color: '#FF4C00',
              textDecoration: 'none',
              transition: 'color 0.2s ease'
            }}>Ana</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      width: '100%',
      minHeight: '100vh',
      backgroundColor: '#000000',
      position: 'relative'
    }}>
      <div style={{
        width: '100%',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem 1rem'
      }}>
        <div style={{
          width: '440px',
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center'
          }}>
            <img
              src="/images/ana_logo.png"
              alt="ANA Logo"
              style={{ height: '48px', width: '48px' }}
            />
            <img
              src="/images/ana_text_dark.svg"
              alt="ANA"
              style={{ marginTop: '1rem', height: '32px', width: 'auto' }}
            />
            <h1 style={{
              marginTop: '1.5rem',
              fontSize: '1.5rem',
              fontWeight: 700,
              color: '#FFFFFF',
              marginBottom: 0
            }}>
              Forgot Password
            </h1>
            <p style={{
              marginTop: '0.5rem',
              fontSize: '0.875rem',
              color: '#9CA3AF',
              marginBottom: 0
            }}>
              Enter your email address and we'll send you a link to reset your password
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                color: '#9CA3AF',
                marginBottom: '0.25rem'
              }}>
                Email address
              </label>
              <input
                {...register('email')}
                type="email"
                disabled={isLoading}
                placeholder="Enter your email address"
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  backgroundColor: 'rgba(15, 15, 15, 0.6)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  color: '#FFFFFF',
                  transition: 'all 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#FF4C00'}
                onBlur={(e) => e.target.style.borderColor = 'rgba(255, 255, 255, 0.15)'}
              />
              {errors.email && (
                <p style={{ marginTop: '0.25rem', fontSize: '0.875rem', color: '#ef4444' }}>
                  {errors.email.message}
                </p>
              )}
            </div>

            {passwordResetRateLimit && (
              <div style={{
                padding: '1rem',
                backgroundColor: 'rgba(251, 191, 36, 0.1)',
                border: '1px solid rgba(251, 191, 36, 0.2)',
                borderRadius: '8px',
                color: '#f59e0b',
                fontSize: '0.875rem',
                textAlign: 'center'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  marginBottom: '0.5rem'
                }}>
                  <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <strong>Too Many Attempts</strong>
                </div>
                <p style={{ margin: '0 0 0.5rem 0', lineHeight: 1.4 }}>
                  {passwordResetRateLimit.message}
                </p>
                <p style={{ 
                  margin: 0, 
                  fontSize: '0.8rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.25rem'
                }}>
                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  Try again in: <strong>{formatTime(countdown)}</strong>
                </p>
              </div>
            )}

            {error && !passwordResetRateLimit && (
              <div style={{
                padding: '1rem',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                borderRadius: '8px',
                color: '#ef4444',
                fontSize: '0.875rem'
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || !!passwordResetRateLimit}
              style={{
                width: '100%',
                padding: '0.5rem 1rem',
                backgroundColor: '#FF4C00',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: (isLoading || passwordResetRateLimit) ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                opacity: (isLoading || passwordResetRateLimit) ? 0.5 : 1
              }}
              onMouseOver={(e) => !(isLoading || passwordResetRateLimit) && (e.currentTarget.style.backgroundColor = '#CC3D00')}
              onMouseOut={(e) => !(isLoading || passwordResetRateLimit) && (e.currentTarget.style.backgroundColor = '#FF4C00')}
            >
              {isLoading ? (
                <>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid rgba(255, 255, 255, 0.2)',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Sending Reset Link...
                </>
              ) : (
                'Send Reset Link'
              )}
            </button>
          </form>

          <div style={{ textAlign: 'center' }}>
            <Link
              to="/login"
              style={{
                color: '#FF4C00',
                textDecoration: 'none',
                fontSize: '0.875rem',
                transition: 'color 0.2s ease'
              }}
            >
              Back to Login
            </Link>
          </div>
        </div>

        <div style={{
          marginTop: '1.5rem',
          textAlign: 'center',
          fontSize: '0.875rem',
          color: '#9CA3AF'
        }}>
          Built with ❤️ by <a href="https://www.openana.ai/" target="_blank" rel="noopener noreferrer" style={{
            color: '#FF4C00',
            textDecoration: 'none',
            transition: 'color 0.2s ease'
          }}>Ana</a>
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default ForgotPassword;
